import { useTranslation } from 'react-i18next';
import type { ReactNode, KeyboardEvent } from 'react';
import { useEffect, useCallback, Fragment, useMemo, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import type { IconButtonPropsExtend } from '@/ui';
import {
  Box,
  List,
  ListItem,
  Panel,
  QuestionMarkIcon,
  InfoOutlinedIcon,
  LegendIcon,
  StorageIcon,
  SearchIcon,
  LayersOutlinedIcon,
} from '@/ui';

import { useMapController, useUIController } from '@/core/controllers/use-controllers';
import { Geolocator } from '@/core/components/geolocator/geolocator';
import type { TypeButtonPanel, TypePanelProps } from '@/ui/panel/panel-types';
import ExportButton from '@/core/components/export/export-modal-button';
import {
  useStoreUIActiveFocusItem,
  useStoreUIActiveTrapGeoView,
  useStoreUIAppbarComponents,
  useStoreUIActiveAppBarTab,
  useStoreUIHiddenTabs,
  useStoreUIAppBarPanelIds,
} from '@/core/stores/states/ui-state';
import { useStoreMapInteraction } from '@/core/stores/states/map-state';
import { useStoreAppGeoviewHTMLElement } from '@/core/stores/states/app-state';
import { useStoreGeoViewConfig, useStoreGeoViewMapId } from '@/core/stores/geoview-store';
import { logger } from '@/core/utils/logger';
import type { AppBarApi } from '@/core/components';
import { Guide, Legend, DetailsPanel, Datapanel, LayersPanel } from '@/core/components';
import Notifications from '@/core/components/notifications/notifications';

import Version from './buttons/version';
import Share from './buttons/share';
import { getSxClasses } from './app-bar-style';
import { enforceArrayOrder } from './app-bar-helper';
import { CONTAINER_TYPE, LIGHTBOX_SELECTORS, TIMEOUT } from '@/core/utils/constant';
import { DEFAULT_APPBAR_CORE, DEFAULT_APPBAR_TABS_ORDER } from '@/api/types/map-schema-types';
import { camelCase, handleEscapeKey } from '@/core/utils/utilities';
import { IconButton } from '@/ui/icon-button/icon-button';

/** Mapping of panel id to its icon and content. */
interface GroupPanelType {
  /** The icon element for the panel button. */
  icon: ReactNode;
  /** The content element rendered inside the panel. */
  content: ReactNode;
}

/** Props for the AppBar component. */
type AppBarProps = {
  api: AppBarApi;
  onScrollShellIntoView: () => void;
};

/** Record of button panel ids to their configuration. */
export interface ButtonPanelType {
  [panelType: string]: TypeButtonPanel;
}

/**
 * Creates an app-bar with buttons that can open a panel.
 *
 * @param props - Properties defined in AppBarProps interface
 * @returns The app bar component
 */
export function AppBar(props: AppBarProps): JSX.Element {
  // Log
  logger.logTraceRender('components/app-bar/app-bar');

  const { api: appBarApi, onScrollShellIntoView } = props;

  // Hooks
  const { t } = useTranslation<string>();
  const theme = useTheme();
  const sxClasses = getSxClasses(theme);

  // get store values and action
  const mapId = useStoreGeoViewMapId();
  const activeModalId = useStoreUIActiveFocusItem().activeElementId;
  const interaction = useStoreMapInteraction();
  const appBarComponents = useStoreUIAppbarComponents();
  const { tabId, isOpen, isFocusTrapped } = useStoreUIActiveAppBarTab();
  const hiddenTabs = useStoreUIHiddenTabs();
  const activeTrapGeoView = useStoreUIActiveTrapGeoView();
  const uiController = useUIController();
  const mapController = useMapController();

  const geoviewElement = useStoreAppGeoviewHTMLElement().querySelector('[id^="mapTargetElement-"]') as HTMLElement;

  // get store config for app bar to add (similar logic as in footer-bar)
  const appBarConfig = useStoreGeoViewConfig()?.appBar;
  const footerBarConfig = useStoreGeoViewConfig()?.footerBar;

  // Read app-bar panel ids from the store (reactive — no event handlers needed)
  const appBarPanelIds = useStoreUIAppBarPanelIds();

  // Ref so the focus-restore effect can read the latest geoviewElement without it being a dep
  const geoviewElementRef = useRef(geoviewElement);
  geoviewElementRef.current = geoviewElement;

  /**
   * Builds the button panels record from store panel ids and api registry, with open/focus state derived from the active tab.
   */
  const memoButtonPanels = useMemo((): ButtonPanelType => {
    // Log
    logger.logTraceUseMemo('APP-BAR - buttonPanels', appBarPanelIds, tabId, isOpen, isFocusTrapped);

    const panels: ButtonPanelType = {};
    appBarPanelIds.forEach((id) => {
      const panel = appBarApi.buttons[id];
      if (panel) {
        // Derive panel status from the store's active app bar tab
        panels[id] = {
          ...panel,
          ...(panel.panel && {
            panel: {
              ...panel.panel,
              status: id === tabId && isOpen,
              isFocusTrapped: id === tabId ? isFocusTrapped : false,
            },
          }),
        };
      }
    });
    return panels;
  }, [appBarPanelIds, appBarApi, tabId, isOpen, isFocusTrapped]);

  // #region REACT HOOKS

  /**
   * Builds the panel components record mapping panel ids to their icon and content.
   */
  const memoPanels = useMemo((): Record<string, GroupPanelType> => {
    // Log
    logger.logTraceUseMemo('APP-BAR - panels');

    // TODO: Refactor - We should find a way to make this 'dictionary of supported components' dynamic.
    if (interaction === 'static') {
      return {};
    }
    return {
      geolocator: { icon: <SearchIcon />, content: <Geolocator key="geolocator" /> },
      guide: { icon: <QuestionMarkIcon />, content: <Guide containerType={CONTAINER_TYPE.APP_BAR} /> },
      details: { icon: <InfoOutlinedIcon />, content: <DetailsPanel containerType={CONTAINER_TYPE.APP_BAR} /> },
      legend: { icon: <LegendIcon />, content: <Legend containerType={CONTAINER_TYPE.APP_BAR} /> },
      layers: { icon: <LayersOutlinedIcon />, content: <LayersPanel containerType={CONTAINER_TYPE.APP_BAR} /> },
      'data-table': { icon: <StorageIcon />, content: <Datapanel containerType={CONTAINER_TYPE.APP_BAR} /> },
    };
  }, [interaction]);

  /**
   * Constructs the AppBar element ID for buttons and panels.
   *
   * @param buttonId - The button identifier
   * @param suffix - Optional suffix to append (e.g., '-panel-btn', '-panel')
   * @returns The full element ID
   */
  const getButtonElementId = useCallback(
    (buttonId: string, suffix?: string): string => {
      return `${mapId}-${CONTAINER_TYPE.APP_BAR}-${buttonId}${suffix ?? ''}`;
    },
    [mapId]
  );

  // #region Handlers

  /**
   * Handles when an app-bar button is clicked.
   *
   * @param buttonId - The id of the clicked button
   */
  const handleButtonClicked = useCallback(
    (buttonId: string): void => {
      // Get the button panel
      const buttonPanel = memoButtonPanels[buttonId];
      uiController.setActiveAppBarTab(buttonId, !buttonPanel.panel?.status, !buttonPanel.panel?.status);
    },
    [memoButtonPanels, uiController]
  );

  /**
   * Handles the general close action of a panel.
   *
   * @param buttonId - The id of the panel button to close
   */
  const handleGeneralCloseClicked = useCallback(
    (buttonId: string): void => {
      // Return focus to the AppBar button that opened this panel
      if (isFocusTrapped) {
        setTimeout(() => {
          document.getElementById(getButtonElementId(buttonId, '-panel-btn'))?.focus();
        }, TIMEOUT.dataPanelLoading);
      }

      uiController.setActiveAppBarTab(buttonId, false, false);
    },
    [uiController, isFocusTrapped, getButtonElementId]
  );

  // #endregion

  /**
   * Gets the panel width based on the tab type.
   *
   * Panels default to 100% width; legend and details panels are set to be slimmer.
   *
   * @param tab - The id of the panel
   * @returns The width percentage for the panel
   */
  const getPanelWidth = useCallback((tab: string): number => {
    let width = 100;

    // set these panels to be slimmer
    if (tab === DEFAULT_APPBAR_CORE.LEGEND || tab === DEFAULT_APPBAR_CORE.DETAILS) {
      width = 30;
    }

    return width;
  }, []);

  /**
   * Handles when the panel opens.
   */
  const handlePanelOpen = useCallback(() => {
    // Do something
  }, []);

  /**
   * Handles when the panel closes.
   */
  const handlePanelClose = useCallback(() => {
    // Hide the marker icon
    mapController.clickMarkerIconHide();
  }, [mapController]);

  /**
   * Restores focus when a panel is closed.
   */
  useEffect(() => {
    // Log
    logger.logTraceUseEffect('APP-BAR - focus restore on close', isOpen, tabId);

    if (!isOpen && tabId) {
      const buttonElementId = `${mapId}-${CONTAINER_TYPE.APP_BAR}-${tabId}-panel-btn`;
      const buttonElement = document.getElementById(buttonElementId);
      if (buttonElement) {
        buttonElement.focus();
      } else {
        const mapCont = geoviewElementRef.current;
        mapCont?.focus();
        if (mapCont?.closest('.geoview-map')?.classList.contains('map-focus-trap')) {
          mapCont.classList.add('keyboard-focus');
        }
      }
    }
  }, [isOpen, tabId, mapId]);

  /**
   * Creates AppBar button panels from configuration tabs.
   */
  useEffect(() => {
    // Log
    logger.logTraceUseEffect('APP-BAR - create group of AppBar buttons');

    // render app bar tabs
    const appBarConfigTabs = appBarConfig?.tabs.core ?? [];
    if (footerBarConfig?.tabs.core === undefined && !appBarConfigTabs.includes('guide')) {
      // inject guide tab if no footer bar config
      appBarConfigTabs.push('guide');
    }

    appBarConfigTabs
      .filter((tab) => DEFAULT_APPBAR_TABS_ORDER.includes(tab) && memoPanels[tab])
      .map((tab): [IconButtonPropsExtend, TypePanelProps, string] => {
        const button: IconButtonPropsExtend = {
          id: tab,
          'aria-label': t(`${camelCase(tab)}.title`),
          tooltip: t(`${camelCase(tab)}.title`),
          tooltipPlacement: 'bottom',
          children: memoPanels[tab].icon,
        };
        const panel: TypePanelProps = {
          panelId: tab,
          type: CONTAINER_TYPE.APP_BAR,
          title: `${camelCase(tab)}.title`,
          icon: memoPanels[tab].icon,
          content: memoPanels[tab].content,
          width: getPanelWidth(tab),
          panelStyles: {
            panelCardContent: { padding: '0' },
          },
        };
        return [button, panel, tab];
      })
      .forEach((appBarTab) => appBarApi.createAppbarPanel(appBarTab[0], appBarTab[1]));
  }, [footerBarConfig?.tabs.core, appBarConfig?.tabs.core, appBarApi, t, memoPanels, geoviewElement, getPanelWidth]);

  // #endregion

  /**
   * Re-order the appbar buttons.
   */
  const { topPanelNames, bottomPanelNames } = useMemo(() => {
    // Log
    logger.logTraceUseMemo('APP-BAR - panels reorder buttons');

    let buttonPanelNames = Object.keys(memoButtonPanels);
    buttonPanelNames = enforceArrayOrder(buttonPanelNames, DEFAULT_APPBAR_TABS_ORDER);
    const topPanel = buttonPanelNames.filter((groupName) => groupName !== DEFAULT_APPBAR_CORE.GUIDE);
    const bottomPanel = buttonPanelNames.filter((groupName) => groupName === DEFAULT_APPBAR_CORE.GUIDE);

    return { topPanelNames: topPanel, bottomPanelNames: bottomPanel };
  }, [memoButtonPanels]);

  /**
   * Renders tab buttons in the app-bar.
   *
   * @param panelNames - The panel names to render as buttons
   * @returns The rendered list of buttons, or null if none are visible
   */
  const renderButtonPanel = (panelNames: string[]): ReactNode => {
    // Map through panel names and create ListItems for visible buttons
    const visibleButtons = panelNames
      .filter((name) => !hiddenTabs.includes(name))
      .map((panelName: string) => {
        // Get the button panel configuration for this panel name
        const buttonPanel = memoButtonPanels[panelName];

        // Only render if the button is explicitly set to visible
        if (buttonPanel?.button.visible !== undefined && buttonPanel?.button.visible) {
          // WCAG - Compute ARIA attributes before rendering
          const isPanelOpen: boolean = tabId === buttonPanel.button.id && isOpen;
          const expandedState: 'true' | 'false' = isPanelOpen ? 'true' : 'false';
          const ariaControls: string | undefined = activeTrapGeoView ? undefined : getButtonElementId(buttonPanel.button.id!, '-panel');
          const ariaExpanded: 'true' | 'false' | undefined = activeTrapGeoView ? undefined : expandedState;

          return (
            <ListItem key={buttonPanel.button.id}>
              <IconButton
                id={getButtonElementId(buttonPanel.button.id!, '-panel-btn')}
                aria-label={t(buttonPanel.button['aria-label'])}
                // In WCAG mode, panels are treated as dialogs because they are focus-trapped, so we set aria-haspopup to dialog to indicate that.
                aria-haspopup={activeTrapGeoView ? 'dialog' : undefined}
                // In default mode, panels are treated as regions, so we use aria-controls and aria-expanded to indicate the relationship and state.
                aria-controls={ariaControls}
                aria-expanded={ariaExpanded}
                tooltipPlacement="right"
                className={`buttonFilled ${tabId === buttonPanel.button.id && isOpen ? 'active' : ''}`}
                size="small"
                onClick={() => handleButtonClicked(buttonPanel.button.id!)}
              >
                {buttonPanel.button.children}
              </IconButton>
            </ListItem>
          );
        }
        // Return null for buttons that aren't visible
        return null;
      })
      // Filter out all null values to get only visible buttons
      .filter(Boolean);

    // Don't render an empty list if there are no visible buttons
    if (visibleButtons.length === 0) {
      return null;
    }

    // Render a single List containing all visible button ListItems
    return <List sx={sxClasses.appBarList}>{visibleButtons}</List>;
  };

  return (
    <Box sx={sxClasses.appBar} className={`interaction-${interaction}`} id={`${mapId}-appBar`} onClick={onScrollShellIntoView}>
      <Box sx={sxClasses.appBarButtons} component="nav" aria-label={t('appbar.navLabel')}>
        {renderButtonPanel(topPanelNames)}
        <Box sx={sxClasses.versionButtonDiv}>
          {renderButtonPanel(bottomPanelNames)}
          <List sx={sxClasses.appBarList}>
            {appBarComponents.includes(DEFAULT_APPBAR_CORE.EXPORT) && interaction === 'dynamic' && (
              <ListItem>
                <ExportButton
                  id={`${mapId}-${CONTAINER_TYPE.APP_BAR}-export-modal-btn`}
                  className={` buttonFilled ${activeModalId === DEFAULT_APPBAR_CORE.EXPORT ? 'active' : ''}`}
                />
              </ListItem>
            )}
            <ListItem>
              <Share />
            </ListItem>
            <ListItem sx={sxClasses.appBarSeparator}>
              <Notifications />
            </ListItem>
            <ListItem>
              <Version />
            </ListItem>
          </List>
        </Box>
      </Box>
      {Object.keys(memoButtonPanels).map((panelName: string) => {
        // get button panel
        const buttonPanel = memoButtonPanels[panelName];
        let content = null;
        if (buttonPanel?.buttonPanelId === DEFAULT_APPBAR_CORE.GEOLOCATOR) {
          content = buttonPanel?.panel?.content ?? '';
        } else if (buttonPanel?.panel) {
          content = (
            <Panel
              panel={buttonPanel.panel}
              button={buttonPanel.button}
              onOpen={handlePanelOpen}
              onClose={handlePanelClose}
              onKeyDown={(event: KeyboardEvent) => {
                // Early exit if lightbox is handling ESC
                if (event.key === 'Escape') {
                  const isLightboxOpen = document.querySelector(LIGHTBOX_SELECTORS.ROOT) !== null;
                  if (isLightboxOpen) {
                    return;
                  }
                }
                handleEscapeKey(
                  event.key,
                  () => {
                    uiController.setActiveAppBarTab(buttonPanel.button?.id ?? '', false, false);
                  },
                  getButtonElementId(buttonPanel.button?.id ?? '', '-panel-btn'),
                  isFocusTrapped
                );
              }}
              onGeneralClose={() => {
                handleGeneralCloseClicked(buttonPanel.button?.id ?? '');
              }}
            />
          );
        }
        // display the panels in the list
        return <Fragment key={panelName}>{content}</Fragment>;
      })}
    </Box>
  );
}
